import React from "react";
import {
  Button,
  Text,
  VStack,
  HStack,
  Badge,
  Accordion,
  Table,
  Box,
  Progress,
} from "@chakra-ui/react";
import { BulkImportResult } from "../../services/Dtos";
import useThemeColors from "../../hooks/useThemeColors";
import CustomModal from "./CustomModal";
import useIcons from "../../hooks/useIcons";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  feedback: BulkImportResult | null;
  isUploading: boolean;
  title?: string;
  loadingText?: string;
}

const BulkImportFeedbackModal: React.FC<Props> = ({
  isOpen,
  onClose,
  feedback,
  isUploading,
  title = "Import Result",
  loadingText = "Uploading and processing file...",
}) => {
  const colors = useThemeColors();
  const icons = useIcons();

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        handleClose={onClose}
        mode="view"
        title={title}
        icon={icons.utility.review}
        handleAction={onClose}
        isLoading={isUploading}
        isDisabled={false}
        hasChanges={!!feedback}
        actionText="Close"
        hideCancelButton={true}
      >
        {isUploading ? (
          <VStack gap={4} align="stretch">
            <Text>{loadingText}</Text>
            <Progress.Root
              size="xs"
              value={null}
              colorPalette={colors.brand.primary}
            >
              <Progress.Track>
                <Progress.Range />
              </Progress.Track>
            </Progress.Root>
          </VStack>
        ) : feedback ? (
          <VStack gap={4} align="stretch">
            <HStack justifyContent="space-around">
              <VStack>
                <Text fontWeight="bold">Total Rows</Text>
                <Badge fontSize="lg">{feedback.totalRows}</Badge>
              </VStack>
              <VStack>
                <Text fontWeight="bold">Successful</Text>
                <Badge colorPalette="green" fontSize="lg">
                  {feedback.successCount}
                </Badge>
              </VStack>
              <VStack>
                <Text fontWeight="bold">Failed</Text>
                <Badge colorPalette="red" fontSize="lg">
                  {feedback.failureCount}
                </Badge>
              </VStack>
            </HStack>

            {feedback.errors && feedback.errors.length > 0 && (
              <Box
                bg={colors.status.errorBg}
                border="1px solid"
                borderColor={colors.status.errorBorder}
                borderRadius="md"
                p={1}
              >
                <Accordion.Root collapsible>
                  <Accordion.Item value="view-errors">
                    <Accordion.ItemTrigger>
                      <Button flex="1" variant="ghost" colorPalette="red">
                        View Errors ({feedback.errors.length})
                      </Button>
                    </Accordion.ItemTrigger>

                    <Accordion.ItemContent>
                      <Table.Root variant="outline" size="sm">
                        <Table.Header
                          bg={colors.status.errorBg}
                          border="1px solid"
                          borderColor={colors.status.errorBorder}
                          borderRadius="md"
                        >
                          <Table.Row>
                            <Table.ColumnHeader fontWeight="bold">
                              Row
                            </Table.ColumnHeader>
                            <Table.ColumnHeader fontWeight="bold">
                              Student Name
                            </Table.ColumnHeader>
                            <Table.ColumnHeader fontWeight="bold">
                              Field
                            </Table.ColumnHeader>
                            <Table.ColumnHeader fontWeight="bold">
                              Error
                            </Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {feedback.errors.map((error, index) => (
                            <Table.Row key={index}>
                              <Table.Cell>{error.rowNumber}</Table.Cell>
                              <Table.Cell>{error.studentName}</Table.Cell>
                              <Table.Cell>{error.field}</Table.Cell>
                              <Table.Cell>{error.error}</Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    </Accordion.ItemContent>
                  </Accordion.Item>
                </Accordion.Root>
              </Box>
            )}
          </VStack>
        ) : (
          <Text>No feedback available.</Text>
        )}
      </CustomModal>
    </>
  );
};

export default BulkImportFeedbackModal;
